import React, { useState, useEffect, useCallback } from 'react';
import authService from '../../../../services/AuthService';

const API_BASE = 'https://server.multisellerpro.com/api/flashpricer';

const COLUMNS = ['SKU', 'COST', 'MIN', 'MAX', 'MAP', 'SALES CHANNEL ID', 'STRATEGY ID', 'FBM SHIPPING COST'];

const COLUMN_WIDTHS = {
    'SKU': '140px',
    'COST': '80px',
    'MIN': '80px',
    'MAX': '80px',
    'MAP': '80px',
    'SALES CHANNEL ID': '130px',
    'STRATEGY ID': '110px',
    'FBM SHIPPING COST': '130px',
};

const emptyForm = () => Object.fromEntries(COLUMNS.map(c => [c, '']));

const SALES_CHANNEL_KEY = 'flashpricer_sales_channel_id';
const STRATEGY_KEY = 'flashpricer_strategy_id';
const SALES_CHANNEL_KEY_WM = 'flashpricer_sales_channel_id_wm';
const STRATEGY_KEY_WM = 'flashpricer_strategy_id_wm';

const SC_PRESETS_KEY = 'flashpricer_sc_presets';
const STRATEGY_PRESETS_KEY = 'flashpricer_strategy_presets';

function loadPresets(key) {
    try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
}

function PresetManager({ type, presets, onUpdate, onClose }) {
    const [name, setName] = useState('');
    const [id, setId] = useState('');
    const label = type === 'sc' ? 'Sales Channel' : 'Strategy';

    const add = () => {
        if (!name.trim() || !id.trim()) return;
        const updated = [...presets, { name: name.trim(), id: id.trim() }];
        onUpdate(updated);
        setName('');
        setId('');
    };

    const remove = (removeId) => {
        onUpdate(presets.filter(p => p.id !== removeId));
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 10000,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    backgroundColor: 'white', borderRadius: '12px',
                    padding: '20px', width: '340px', maxHeight: '80vh',
                    overflowY: 'auto',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <span style={{ fontWeight: '700', fontSize: '13px', color: '#111827' }}>
                        Manage {label} Presets
                    </span>
                    <button
                        onClick={onClose}
                        style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}
                    >×</button>
                </div>

                {presets.length === 0 && (
                    <div style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', padding: '12px 0' }}>
                        No presets yet. Add one below.
                    </div>
                )}
                {presets.map(p => (
                    <div key={p.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 10px', marginBottom: '6px',
                        backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb',
                    }}>
                        <div>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{p.name}</div>
                            <div style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace', marginTop: '2px' }}>{p.id}</div>
                        </div>
                        <button
                            onClick={() => remove(p.id)}
                            style={{
                                background: 'none', border: '1px solid #fca5a5',
                                color: '#dc2626', cursor: 'pointer', fontSize: '11px',
                                fontWeight: '600', borderRadius: '5px', padding: '3px 8px',
                            }}
                        >
                            Remove
                        </button>
                    </div>
                ))}

                <div style={{
                    marginTop: '14px', borderTop: '1px solid #e5e7eb', paddingTop: '14px',
                    display: 'flex', flexDirection: 'column', gap: '8px',
                }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '2px' }}>
                        Add new preset
                    </div>
                    <input
                        placeholder="Name (e.g. My Amazon US Store)"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px' }}
                    />
                    <input
                        placeholder="ID value"
                        value={id}
                        onChange={e => setId(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') add(); }}
                        style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', fontFamily: 'monospace' }}
                    />
                    <button
                        onClick={add}
                        disabled={!name.trim() || !id.trim()}
                        style={{
                            padding: '7px', fontSize: '12px', fontWeight: '700',
                            backgroundColor: (!name.trim() || !id.trim()) ? '#9ca3af' : '#2563eb',
                            color: 'white', border: 'none', borderRadius: '6px',
                            cursor: (!name.trim() || !id.trim()) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        + Add Preset
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function FlashPricerComponent({ asin, basicData, platform = 'amazon' }) {
    const activeSalesChannelKey = platform === 'walmart' ? SALES_CHANNEL_KEY_WM : SALES_CHANNEL_KEY;
    const activeStrategyKey = platform === 'walmart' ? STRATEGY_KEY_WM : STRATEGY_KEY;

    const [rows, setRows] = useState([]);
    const [publicUrl, setPublicUrl] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [editingSku, setEditingSku] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [deletingSkus, setDeletingSkus] = useState(new Set());
    const [error, setError] = useState('');
    const [quickAdded, setQuickAdded] = useState(false);
    const [skuSearch, setSkuSearch] = useState('');

    const [scPresets, setScPresets] = useState(() => loadPresets(SC_PRESETS_KEY));
    const [strategyPresets, setStrategyPresets] = useState(() => loadPresets(STRATEGY_PRESETS_KEY));
    const [presetManager, setPresetManager] = useState(null);

    const updateScPresets = (list) => {
        localStorage.setItem(SC_PRESETS_KEY, JSON.stringify(list));
        setScPresets(list);
    };
    const updateStrategyPresets = (list) => {
        localStorage.setItem(STRATEGY_PRESETS_KEY, JSON.stringify(list));
        setStrategyPresets(list);
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await authService.authenticatedFetch(`${API_BASE}/rows`);
            const data = await res.json();
            if (res.ok) {
                setRows(data.rows || []);
                setPublicUrl(data.public_url || '');
            }
        } catch (e) {
            setError('Failed to load FlashPricer data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openAddForm = () => {
        const prefilled = emptyForm();
        if (asin) prefilled['SKU'] = asin;
        if (basicData?.cost_price) prefilled['COST'] = String(basicData.cost_price);
        if (basicData?.fbm_fee) prefilled['FBM SHIPPING COST'] = String(basicData.fbm_fee);
        prefilled['SALES CHANNEL ID'] = localStorage.getItem(activeSalesChannelKey) || '';
        prefilled['STRATEGY ID'] = localStorage.getItem(activeStrategyKey) || '';
        setForm(prefilled);
        setEditingSku(null);
        setShowForm(true);
        setError('');
    };

    const openEditForm = (row) => {
        setForm({ ...emptyForm(), ...row });
        setEditingSku(row.SKU);
        setShowForm(true);
        setError('');
    };

    const handleSave = async () => {
        if (!form['SKU'].trim()) {
            setError('SKU is required');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const res = await authService.authenticatedFetch(`${API_BASE}/rows`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (res.ok) {
                setRows(data.rows || []);
                setPublicUrl(data.public_url || '');
                setShowForm(false);
                setForm(emptyForm());
                setEditingSku(null);
            } else {
                setError(data.error || 'Failed to save');
            }
        } catch (e) {
            setError('Network error');
        } finally {
            setSaving(false);
        }
    };

    const quickAdd = () => {
        if (!asin) return;
        const prefilled = emptyForm();
        prefilled['SKU'] = asin;
        if (basicData?.cost_price) prefilled['COST'] = String(basicData.cost_price);
        if (basicData?.fbm_fee) prefilled['FBM SHIPPING COST'] = String(basicData.fbm_fee);
        prefilled['SALES CHANNEL ID'] = localStorage.getItem(activeSalesChannelKey) || '';
        prefilled['STRATEGY ID'] = localStorage.getItem(activeStrategyKey) || '';
        setForm(prefilled);
        setEditingSku(null);
        setShowForm(true);
        setError('');
    };

    const handleDelete = async (sku) => {
        if (!window.confirm(`Delete row for SKU "${sku}"?`)) return;
        setDeletingSkus(prev => new Set([...prev, sku]));
        try {
            const res = await authService.authenticatedFetch(
                `${API_BASE}/rows/${encodeURIComponent(sku)}`,
                { method: 'DELETE' }
            );
            const data = await res.json();
            if (res.ok) {
                setRows(data.rows || []);
                setPublicUrl(data.public_url || '');
            }
        } catch (e) {
            setError('Failed to delete');
        } finally {
            setDeletingSkus(prev => { const s = new Set(prev); s.delete(sku); return s; });
        }
    };

    const copyLink = async () => {
        if (!publicUrl) return;
        await navigator.clipboard.writeText(publicUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const existingSkus = new Set(rows.map(r => r.SKU));
    const currentAsinInList = asin && existingSkus.has(asin);

    const filteredRows = skuSearch.trim()
        ? rows.filter(r => r.SKU?.toLowerCase().includes(skuSearch.trim().toLowerCase()))
        : rows;

    return (
        <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
        }}>
            {/* Header */}
            <div style={{
                padding: '10px 16px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '8px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>
                        ⚡ FlashPricer
                    </span>
                    <span style={{
                        fontSize: '11px', color: '#6b7280',
                        backgroundColor: '#f3f4f6', padding: '2px 7px', borderRadius: '20px'
                    }}>
                        {rows.length} row{rows.length !== 1 ? 's' : ''}
                    </span>
                </div>

                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {asin && (
                        <button
                            onClick={quickAdd}
                            disabled={saving || currentAsinInList}
                            style={{
                                padding: '4px 12px', fontSize: '11px', fontWeight: '700',
                                backgroundColor: currentAsinInList ? '#d1fae5' : (quickAdded ? '#10b981' : '#2563eb'),
                                color: currentAsinInList ? '#065f46' : 'white',
                                border: 'none', borderRadius: '6px', cursor: currentAsinInList ? 'default' : 'pointer',
                                transition: 'all 0.2s',
                            }}
                            title={currentAsinInList ? 'ASIN already in list' : `Add ${asin} as SKU`}
                        >
                            {currentAsinInList ? `✓ ${asin} in list` : quickAdded ? '✓ Added!' : `+ Add ${asin}`}
                        </button>
                    )}

                    {!asin && (
                        <button
                            onClick={openAddForm}
                            style={{
                                padding: '4px 12px', fontSize: '11px', fontWeight: '600',
                                backgroundColor: '#f3f4f6', color: '#374151',
                                border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                            }}
                        >
                            + Full Form
                        </button>
                    )}

                    {publicUrl && rows.length > 0 && (
                        <button
                            onClick={copyLink}
                            style={{
                                padding: '4px 12px', fontSize: '11px', fontWeight: '600',
                                backgroundColor: copied ? '#10b981' : '#7c3aed',
                                color: 'white',
                                border: 'none', borderRadius: '6px', cursor: 'pointer',
                                transition: 'all 0.2s',
                            }}
                            title={publicUrl}
                        >
                            {copied ? '✓ Copied!' : '🔗 Copy Link'}
                        </button>
                    )}
                </div>
            </div>

            {/* Error */}
            {error && (
                <div style={{ padding: '8px 16px', backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: '12px' }}>
                    {error}
                </div>
            )}

            {/* Add / Edit Form */}
            {showForm && (
                <div style={{
                    padding: '14px 16px',
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb',
                }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '10px' }}>
                        {editingSku ? `Edit SKU: ${editingSku}` : 'Add New Row'}
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                        gap: '8px',
                        marginBottom: '10px',
                    }}>
                        {COLUMNS.map(col => (
                            <div key={col}>
                                <label style={{ fontSize: '10px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '3px' }}>
                                    {col}{col === 'SKU' ? ' *' : ''}
                                </label>

                                {col === 'SALES CHANNEL ID' || col === 'STRATEGY ID' ? (
                                    <div style={{ display: 'flex', gap: '4px', minWidth: 0 }}>
                                        <select
                                            value={form[col]}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setForm(prev => ({ ...prev, [col]: val }));
                                                if (col === 'SALES CHANNEL ID') localStorage.setItem(activeSalesChannelKey, val);
                                                if (col === 'STRATEGY ID') localStorage.setItem(activeStrategyKey, val);
                                            }}
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                width: 0,
                                                padding: '5px 8px',
                                                border: '1px solid #d1d5db', borderRadius: '6px',
                                                fontSize: '12px', backgroundColor: 'white', color: '#111827',
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            <option value="">— select preset —</option>
                                            {(col === 'SALES CHANNEL ID' ? scPresets : strategyPresets).map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => setPresetManager(col === 'SALES CHANNEL ID' ? 'sc' : 'strategy')}
                                            title={`Manage ${col} presets`}
                                            style={{
                                                padding: '4px 7px', fontSize: '13px',
                                                backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
                                                borderRadius: '6px', cursor: 'pointer', flexShrink: 0,
                                            }}
                                        >
                                            ⚙️
                                        </button>
                                    </div>
                                ) : (
                                    <input
                                        type="text"
                                        value={form[col]}
                                        onChange={e => {
                                            const val = e.target.value;
                                            setForm(prev => ({ ...prev, [col]: val }));
                                        }}
                                        style={{
                                            width: '100%', padding: '5px 8px',
                                            border: '1px solid #d1d5db', borderRadius: '6px',
                                            fontSize: '12px', boxSizing: 'border-box',
                                            backgroundColor: 'white',
                                        }}
                                        placeholder={col === 'SKU' ? 'e.g. B0XXXXXX' : ''}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                                padding: '6px 18px', fontSize: '12px', fontWeight: '700',
                                backgroundColor: saving ? '#9ca3af' : '#2563eb',
                                color: 'white', border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {saving ? 'Saving…' : (editingSku ? 'Update Row' : 'Add Row')}
                        </button>
                        <button
                            onClick={() => { setShowForm(false); setForm(emptyForm()); setEditingSku(null); setError(''); }}
                            style={{
                                padding: '6px 14px', fontSize: '12px',
                                backgroundColor: '#f3f4f6', color: '#374151',
                                border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Table */}
            {loading ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                    Loading…
                </div>
            ) : rows.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                    No rows yet. Add this ASIN or use Full Form to add your first row.
                </div>
            ) : (
                <>
                    {/* SKU Search Bar */}
                    <div style={{
                        padding: '8px 16px',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#fafafa',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        <div style={{ position: 'relative', flex: 1, maxWidth: '260px' }}>
                            <span style={{
                                position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)',
                                fontSize: '12px', color: '#9ca3af', pointerEvents: 'none',
                            }}>
                                🔍
                            </span>
                            <input
                                type="text"
                                value={skuSearch}
                                onChange={e => setSkuSearch(e.target.value)}
                                placeholder="Filter by SKU…"
                                style={{
                                    width: '100%',
                                    padding: '5px 8px 5px 26px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    boxSizing: 'border-box',
                                    backgroundColor: 'white',
                                    outline: 'none',
                                    fontFamily: 'monospace',
                                }}
                            />
                        </div>
                        {skuSearch && (
                            <span style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                {filteredRows.length} of {rows.length}
                            </span>
                        )}
                        {skuSearch && (
                            <button
                                onClick={() => setSkuSearch('')}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    fontSize: '14px', color: '#9ca3af', lineHeight: 1, padding: '0 2px',
                                }}
                                title="Clear search"
                            >
                                ×
                            </button>
                        )}
                    </div>

                    <div style={{ overflowX: 'auto', maxHeight: '350px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', tableLayout: 'auto' }}>
                            <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                    {COLUMNS.map(col => (
                                        <th key={col} style={{
                                            padding: '7px 10px',
                                            textAlign: 'left',
                                            fontSize: '10px', fontWeight: '700',
                                            color: '#6b7280', textTransform: 'uppercase',
                                            borderBottom: '1px solid #e5e7eb',
                                            whiteSpace: 'nowrap',
                                            minWidth: COLUMN_WIDTHS[col] || '80px',
                                        }}>
                                            {col}
                                        </th>
                                    ))}
                                    <th style={{
                                        padding: '7px 10px', fontSize: '10px', fontWeight: '700',
                                        color: '#6b7280', textTransform: 'uppercase',
                                        borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap',
                                    }}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={COLUMNS.length + 1}
                                            style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}
                                        >
                                            No SKUs match "{skuSearch}"
                                        </td>
                                    </tr>
                                ) : (
                                    filteredRows.map((row, idx) => {
                                        const scName = scPresets.find(p => p.id === row['SALES CHANNEL ID'])?.name;
                                        const strName = strategyPresets.find(p => p.id === row['STRATEGY ID'])?.name;

                                        return (
                                            <tr
                                                key={row.SKU + idx}
                                                style={{
                                                    borderBottom: idx < filteredRows.length - 1 ? '1px solid #f3f4f6' : 'none',
                                                    backgroundColor: row.SKU === asin ? '#f0fdf4' : 'white',
                                                }}
                                            >
                                                {COLUMNS.map(col => {
                                                    let display = row[col];
                                                    if (col === 'SALES CHANNEL ID' && scName) display = scName;
                                                    if (col === 'STRATEGY ID' && strName) display = strName;
                                                    return (
                                                        <td key={col} style={{
                                                            padding: '6px 10px',
                                                            fontSize: '12px',
                                                            color: col === 'SKU' ? '#111827' : '#6b7280',
                                                            fontWeight: col === 'SKU' ? '600' : '400',
                                                            fontFamily: 'monospace',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {display || <span style={{ color: '#d1d5db' }}>—</span>}
                                                        </td>
                                                    );
                                                })}
                                                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                                    <div style={{ display: 'flex', gap: '4px' }}>
                                                        <button
                                                            onClick={() => openEditForm(row)}
                                                            style={{
                                                                padding: '2px 8px', fontSize: '10px', fontWeight: '600',
                                                                backgroundColor: '#eff6ff', color: '#2563eb',
                                                                border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer',
                                                            }}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(row.SKU)}
                                                            disabled={deletingSkus.has(row.SKU)}
                                                            style={{
                                                                padding: '2px 8px', fontSize: '10px', fontWeight: '600',
                                                                backgroundColor: '#fef2f2', color: '#dc2626',
                                                                border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer',
                                                            }}
                                                        >
                                                            {deletingSkus.has(row.SKU) ? '…' : 'Del'}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Preset Manager Modal */}
            {presetManager && (
                <PresetManager
                    type={presetManager}
                    presets={presetManager === 'sc' ? scPresets : strategyPresets}
                    onUpdate={presetManager === 'sc' ? updateScPresets : updateStrategyPresets}
                    onClose={() => setPresetManager(null)}
                />
            )}
        </div>
    );
}